// Tiny badge that shows a saved deck's tournament-proximity score on list
// + summary views. Color-graded so you can scan for "tournament-worthy"
// decks at a glance without sorting.
//
// Thresholds (arbitrary, tuned to the current corpus):
//   60+ → accent (green-ish, this is close to a real tier list)
//   40-60 → muted (recognizably shell-shaped but not tier)
//   <40 → subtle (brew; deck shape is far from the corpus)

import { Trophy } from "lucide-react";
import { cn } from "../lib/cn";

interface Props {
  score?: number | null;
  size?: "sm" | "md";
  /** When true, render a dim placeholder instead of nothing so the column
   *  width is stable across rows that are / aren't scored yet. */
  placeholderIfMissing?: boolean;
}

function toneFor(score: number): "accent" | "muted" | "subtle" {
  if (score >= 60) return "accent";
  if (score >= 40) return "muted";
  return "subtle";
}

export default function ProximityBadge({ score, size = "sm", placeholderIfMissing }: Props) {
  if (score == null) {
    if (!placeholderIfMissing) return null;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-md border border-border bg-bg-raised/40 text-fg-subtle font-mono tabular-nums",
          size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs",
        )}
        title="Not yet scored"
      >
        <Trophy size={size === "sm" ? 10 : 12} strokeWidth={1.75} />
        <span>—</span>
      </span>
    );
  }
  const tone = toneFor(score);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border font-mono tabular-nums",
        size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs",
        tone === "accent" && "bg-accent/10 border-accent/40 text-accent",
        tone === "muted" && "bg-bg-raised border-border text-fg-muted",
        tone === "subtle" && "bg-bg-raised/40 border-border text-fg-subtle",
      )}
      title={`Tournament proximity ${score.toFixed(1)} / 100`}
    >
      <Trophy size={size === "sm" ? 10 : 12} strokeWidth={1.75} />
      <span>{score.toFixed(0)}</span>
    </span>
  );
}
